import { BankOutlined } from '@ant-design/icons'
import classNames from 'classnames'
import { Suspense, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import AtomTable from '@/components/Atoms/AtomTable/AtomTable'
import Loader from '@/components/Atoms/Loader/Loader'
import { useUserInfoStore } from '@/stores/user/userInfoStore'
import { OrganisationInterface } from '@/types'
import api from '@/utils/api/api'
import { safeLazy } from '@/utils/app/safeLazy'

import { BANK_ACCOUNTS_COLUMN_WIDTHS, COLUMNS } from './BankAccounts.config'
import cn from './BankAccounts.module.scss'

const BankAccountCreationModal = safeLazy(
  () =>
    import(
      '@/components/EntityCreationModals/BankAccountCreationModal/BankAccountCreationModal'
    )
)

let preloadPromise: Promise<
  typeof import('@/components/EntityCreationModals/BankAccountCreationModal/BankAccountCreationModal')
> | null = null
function preloadModal() {
  if (!preloadPromise) {
    preloadPromise = import(
      '@/components/EntityCreationModals/BankAccountCreationModal/BankAccountCreationModal'
    )
  }
  return preloadPromise
}

function BankAccounts() {
  const [org, setOrg] = useState({} as OrganisationInterface)
  const [open, setOpen] = useState<boolean>(false)
  const [loading, setLoading] = useState(false)

  const {
    selectedOrganisation,
    organisation,
    bankAccountsDto,
    setBankAccountsDto,
  } = useUserInfoStore((state) => state)

  const { code } = useParams()
  const isSelectedOrganisation =
    Object.keys(selectedOrganisation).length > 0 &&
    String(selectedOrganisation.code) == code

  useEffect(() => {
    if (
      Object.keys(selectedOrganisation).length &&
      String(selectedOrganisation.code) == code
    ) {
      setOrg(selectedOrganisation)
    } else {
      setOrg(organisation)
    }
  }, [code, organisation, selectedOrganisation])

  const fetchBankAccounts = async () => {
    if (!org.id) return

    setLoading(true)
    try {
      const response = await api.get(`/organisations/${org.id}/bank-accounts`)
      setBankAccountsDto(response.data.bankAccounts || response.data)
    } catch (error) {
      console.error('Error fetching bank accounts:', error)
      setBankAccountsDto([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (org.id) {
      setBankAccountsDto([])
      fetchBankAccounts()
    }
  }, [org.id])

  const handleOpenModal = async () => {
    await preloadModal()
    setOpen(true)
  }

  return (
    <section
      className={classNames(
        cn.bankAccounts,
        isSelectedOrganisation && cn.selectedOrganisation
      )}
    >
      <div className={cn.inner}>
        <div className={cn.bankAccountCreationButtonContainer}>
          <button onMouseEnter={preloadModal} onClick={handleOpenModal}>
            <div>
              <BankOutlined />
            </div>
            Создать банковский счёт
          </button>
        </div>

        {open && (
          <Suspense fallback={null}>
            <BankAccountCreationModal
              open={open}
              setOpen={setOpen}
              onSuccess={fetchBankAccounts}
            />
          </Suspense>
        )}

        {org.id ? (
          loading ? (
            <div className={cn.loadingContainer}>
              <Loader size={20} />
            </div>
          ) : bankAccountsDto.length > 0 ? (
            <AtomTable
              ariaLabel="Банковские счета"
              data={bankAccountsDto}
              columns={COLUMNS}
              getRowId={(bankAccount) => bankAccount.id}
              columnWidths={BANK_ACCOUNTS_COLUMN_WIDTHS}
              isOrganisationSelected={isSelectedOrganisation}
            />
          ) : (
            <p className={cn.emptyMessage}>Нет банковских счетов</p>
          )
        ) : (
          <div className={cn.loadingContainer}>
            <Loader size={20} />
          </div>
        )}
      </div>
    </section>
  )
}

export default BankAccounts
